import { useState, useEffect, useRef } from "react"
import { doc, addDoc, updateDoc, deleteDoc, getDocs, collection } from "firebase/firestore"
import { db } from "../../services/firebase"
import { LABEL_S, INPUT_S, BTN_PRIMARY, BTN_GHOST, PROVINCIAS } from "../../utils/adminConstants"
import Modal from "./Modal"

// ─────────────────────────────────────────────────
// Helpers de parseo DNI — corregidos para OCR MRZ
// ─────────────────────────────────────────────────

// Convierte zona numérica: OCR confunde letras con dígitos
function fixDigitos(s) {
  return s
    .replace(/O/g,"0").replace(/Q/g,"0")
    .replace(/I/g,"1").replace(/L/g,"1")
    .replace(/Z/g,"2")
    .replace(/B/g,"8")
    .replace(/S/g,"5").replace(/G/g,"6")
}

// Convierte zona de relleno: OCR confunde < con K, L, C, (, etc.
function fixRelleno(s) {
  // Solo caracteres que NUNCA aparecen en nombres argentinos
  return s.replace(/[JQ()\[\]{} ]/g, "<")
}

function parsearFechaDNI(str) {
  if (!str) return ""
  str = str.trim()
  // YYYY-MM-DD ya correcto
  if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return str
  // DD/MM/YYYY
  const slash = str.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/)
  if (slash) return `${slash[3]}-${slash[2].padStart(2,"0")}-${slash[1].padStart(2,"0")}`
  // MRZ AAMMDD (6 dígitos)
  const limpio = fixDigitos(str).replace(/[^0-9]/g,"")
  const mrz = limpio.match(/^(\d{2})(\d{2})(\d{2})$/)
  if (mrz) {
    const anio  = Number(mrz[1]) > 30 ? "19"+mrz[1] : "20"+mrz[1]
    const mes   = Number(mrz[2])
    const dia   = Number(mrz[3])
    // Validar que sea una fecha real antes de devolver
    if (mes < 1 || mes > 12 || dia < 1 || dia > 31) return ""
    return `${anio}-${mrz[2]}-${mrz[3]}`
  }
  return ""
}

function parsearPDF417(texto) {
  const sep = texto.includes("@") ? "@" : "|"
  const partes = texto.split(sep).map(p => p.trim()).filter((p, i) => !(i === 0 && p === ""))
  if (partes.length < 6) throw new Error("Formato PDF417 no reconocido")
  return {
    apellido:   partes[0] || "",
    nombre:     partes[1] || "",
    sexo:       partes[2] === "M" ? "M" : partes[2] === "F" ? "F" : "",
    dni:        (partes[3] || "").replace(/\D/g, ""),
    nacimiento: parsearFechaDNI(partes[5] || ""),
  }
}

async function leerPDF417(archivo) {
  const { BrowserMultiFormatReader, DecodeHintType, BarcodeFormat } = await import("@zxing/library")
  const hints = new Map()
  hints.set(DecodeHintType.POSSIBLE_FORMATS, [BarcodeFormat.PDF_417])
  hints.set(DecodeHintType.TRY_HARDER, true)
  const reader = new BrowserMultiFormatReader(hints)
  const url = URL.createObjectURL(archivo)
  try {
    const result = await reader.decodeFromImageUrl(url)
    return parsearPDF417(result.getText())
  } finally {
    URL.revokeObjectURL(url)
  }
}

function parsearMRZ(lineas) {
  const norm = s => (s || "").replace(/\s/g,"").toUpperCase()

  // Identificar cada línea por su patrón característico
  let l1 = "", l2 = "", l3 = ""
  for (const l of lineas.map(norm)) {
    if (/^[1I][D][A-Z]{3}/.test(l) || l.startsWith("IDARG") || l.startsWith("1DARG")) {
      l1 = l
    } else if (/^[0-9]{5}/.test(fixDigitos(l))) {
      // L2 empieza con 6 dígitos (nacimiento AAMMDD)
      l2 = l
    } else if (l.includes("<<") || /[A-Z]{4,}/.test(l)) {
      // L3 tiene nombre — siempre incluye << como separador
      l3 = l
    }
  }

  // Fallback posicional si alguna no se detectó
  if (!l1) l1 = norm(lineas[0])
  if (!l2) l2 = norm(lineas[1])
  if (!l3) l3 = norm(lineas[2])

  if (!l1 && !l2 && !l3) throw new Error("No se pudieron leer las 3 líneas MRZ")

  // ── L1: DNI — zona 5–12, puramente numérica ──
  const dni = fixDigitos(l1.slice(5, 13)).replace(/[^0-9]/g, "")

  // ── L2: nacimiento [0–5], sexo [7], vencimiento [8–13] ──
  const nacimiento  = parsearFechaDNI(fixDigitos(l2.slice(0, 6)))
  const sexoChar    = l2[7] || ""
  const sexo        = ["F","E","P"].includes(sexoChar) ? "F"
                    : ["M","N","W"].includes(sexoChar) ? "M"
                    : ""
  const vencimiento = parsearFechaDNI(fixDigitos(l2.slice(8, 14)))

  // ── L3: APELLIDO<<NOMBRE<<<… ─────────────────
  // Separador real: "<<". OCR puede leer < como K o L.
  // Split en 2+ consecutivos de {<, K, L} — protege K/L solos dentro de nombres.
  const partes3  = l3.split(/[<KL]{2,}/)
  const toTitleCase = s => s.trim().toLowerCase().replace(/\b\w/g, c => c.toUpperCase())

  const apellido = toTitleCase((partes3[0] || "").replace(/[<KL]+$/,"").replace(/[< ]+/g," "))
  const nombre   = toTitleCase((partes3[1] || "").replace(/[<KL]+$/,"").replace(/[< ]+/g," "))

  return { apellido, nombre, dni, nacimiento, vencimiento, sexo }
}

async function leerMRZ(archivo) {
  const Tesseract = await import("tesseract.js")
  const { data: { text } } = await Tesseract.recognize(archivo, "eng", {
    tessedit_char_whitelist: "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789<",
    tessedit_pageseg_mode:   "6",
    tessedit_ocr_engine_mode:"1",
  })

  // ── Normalizar con criterio MRZ estricto ─────────────────────────────────
  const esCandidataMRZ = normalized => {
    if (normalized.length < 15) return false
    const contenido = normalized.replace(/</g, "")
    return contenido.length >= 10
  }

  const normLinea = l =>
    l.toUpperCase().trim()
     .replace(/[^A-Z0-9<]/g, "<")
     .replace(/\s+/g, "")

  const candidatas = text
    .split("\n")
    .map(normLinea)
    .filter(esCandidataMRZ)
    .sort((a, b) => b.length - a.length)
    .slice(0, 6)

  // ── Identificar las 3 líneas MRZ por patrón estricto ──────────────────────
  const l1c = candidatas.find(l =>
    /^[1I][D][A-Z]{3}/.test(l) || l.startsWith("IDARG") || l.startsWith("1DARG")
  ) || ""

  const l2c = candidatas.find(l =>
    l !== l1c && /^[0-9]{5}/.test(fixDigitos(l))
  ) || ""

  const l3c = candidatas.find(l =>
    l !== l1c && l !== l2c &&
    /^[A-Z<]+$/.test(l) &&
    (l.includes("<<") || /[<KL]{2}/.test(l))
  ) || ""

  if (!l1c || !l2c || !l3c) throw new Error("No se encontraron las 3 líneas MRZ en la imagen")
  return parsearMRZ([l1c, l2c, l3c])
}

// ─────────────────────────────────────────────────
// Helper CUIL
// ─────────────────────────────────────────────────
function calcularVerificadorCUIL(prefijo, dni) {
  const numero = String(prefijo).padStart(2,"0") + String(dni).padStart(8,"0")
  if (numero.length !== 10) return ""
  const coef = [5,4,3,2,7,6,5,4,3,2]
  const suma = numero.split("").reduce((acc,d,i) => acc + (parseInt(d)||0)*coef[i], 0)
  const resto = suma % 11
  if (resto === 0) return "0"
  if (resto === 1) return "9"
  return String(11 - resto)
}

// ─────────────────────────────────────────────────
// ModalNuevoCliente
// ─────────────────────────────────────────────────
export function ModalNuevoCliente({ onGuardado, onCerrar }) {
  const [form, setForm] = useState({
    nombre:"", apellido:"", dni:"", cuil:"", telefono:"", email:"",
    nacimiento:"", vencimiento:"", sexo:"", origen:"", ciudad:"", ciudad_custom:""
  })
  const [guardando, setGuardando]   = useState(false)
  const [error, setError]           = useState("")
  const [leyendoDNI, setLeyendoDNI] = useState(false)
  const [dniLeido, setDniLeido]     = useState(false)
  const [previaDNI, setPreviaDNI]   = useState(null)
  const [formatoDNI, setFormatoDNI]   = useState("mrz") // "mrz" | "pdf417"
  const [mrzManual, setMrzManual]     = useState("")     // fallback: texto pegado
  const [mrzFallback, setMrzFallback] = useState(false)
  const inputFileRef                  = useRef(null)
  const [cuilPrefijo, setCuilPrefijo] = useState("")
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }))

  // Auto-setear prefijo cuando llega el sexo
  useEffect(() => {
    if (!cuilPrefijo && form.sexo) {
      if (form.sexo === "M") setCuilPrefijo("20")
      else if (form.sexo === "F") setCuilPrefijo("27")
      else setCuilPrefijo("23")
    }
  }, [form.sexo])

  // Recalcular CUIL cuando cambia prefijo o DNI
  useEffect(() => {
    if (cuilPrefijo.length === 2 && form.dni.length >= 7) {
      const check = calcularVerificadorCUIL(cuilPrefijo, form.dni)
      set("cuil", cuilPrefijo + form.dni.padStart(8,"0") + check)
    }
  }, [cuilPrefijo, form.dni])
  const sel = { ...INPUT_S, cursor:"pointer" }

  // Parsear MRZ pegado manualmente en tiempo real
  function handleMrzManual(texto) {
    setMrzManual(texto)
    setError(""); setDniLeido(false)
    const lineas = texto.split("\n").map(l => l.trim().toUpperCase().replace(/[^A-Z0-9<]/g,"<")).filter(l => l.length >= 15)
    if (lineas.length < 3) return
    try {
      const datos = parsearMRZ(lineas)
      setForm(p => ({
        ...p,
        nombre:      datos.nombre      || p.nombre,
        apellido:    datos.apellido    || p.apellido,
        dni:         datos.dni         || p.dni,
        nacimiento:  datos.nacimiento  || p.nacimiento,
        vencimiento: datos.vencimiento || p.vencimiento,
        sexo:        datos.sexo        || p.sexo,
      }))
      setDniLeido(true)
    } catch(e) { /* esperar más líneas */ }
  }

  async function handleArchivoDNI(e) {
    const archivo = e.target.files?.[0]
    if (!archivo) return
    setLeyendoDNI(true); setError(""); setDniLeido(false)

    const readerPrevia = new FileReader()
    readerPrevia.onload = ev => setPreviaDNI(ev.target.result)
    readerPrevia.readAsDataURL(archivo)

    try {
      const datos = formatoDNI === "pdf417"
        ? await leerPDF417(archivo)
        : await leerMRZ(archivo)

      setForm(p => ({
        ...p,
        nombre:      datos.nombre      || p.nombre,
        apellido:    datos.apellido    || p.apellido,
        dni:         datos.dni         || p.dni,
        nacimiento:  datos.nacimiento  || p.nacimiento,
        vencimiento: datos.vencimiento || p.vencimiento,
        sexo:        datos.sexo        || p.sexo,
      }))
      setDniLeido(true)
    } catch(err) {
      console.error(err)
      if (formatoDNI === "mrz") {
        setError("El OCR no pudo leer el MRZ automáticamente. Pegá las 3 líneas del dorso en el campo de abajo.")
        setMrzFallback(true)
      } else {
        setError("No se encontró el código de barras PDF417. Verificá que la foto sea del dorso y esté nítida, o completá los campos a mano.")
      }
    } finally {
      setLeyendoDNI(false)
      if (inputFileRef.current) inputFileRef.current.value = ""
    }
  }

  async function handleGuardar() {
    if (!form.nombre.trim() || !form.apellido.trim())
      return setError("Nombre y apellido son obligatorios.")
    setGuardando(true); setError("")
    try {
      const { ciudad_custom, ...rest } = form
      const nuevoCliente = {
        ...rest,
        ciudad: form.ciudad === "Otra" ? (ciudad_custom || "") : form.ciudad,
        puntos_aporte:    0, puntos_feedback:  0,
        puntos_referidos: 0, puntos_compra_ref:0,
        puntos_cumpleanos:0, puntos_usados:    0,
        contrataciones:   0, creado_en: new Date(), creado_manual: true,
      }
      const ref = await addDoc(collection(db, "usuarios_sheets"), nuevoCliente)
      onGuardado({ id: ref.id, ...nuevoCliente })
    } catch(e) {
      console.error(e); setError("Error al guardar.")
    } finally { setGuardando(false) }
  }

  return (
    <Modal titulo="Nuevo cliente" subtitulo="Alta manual" onCerrar={onCerrar} ancho="600px">
      <div style={{ display:"flex", flexDirection:"column", gap:"14px" }}>

        {/* ── Lector DNI ── */}
        <div style={{ background:"#0f2340", border:"1px solid #1e3a5f", borderRadius:"12px", padding:"16px" }}>

          {/* Título */}
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:"12px" }}>
            <div style={{ fontSize:"0.85rem", fontWeight:700, color:"#3b82f6" }}>🪪 Leer DNI automáticamente</div>
            {dniLeido && <span style={{ fontSize:"0.8rem", color:"#10b981", fontWeight:700 }}>✓ Datos cargados</span>}
          </div>

          {/* Selector de formato */}
          <div style={{ display:"flex", gap:"8px", marginBottom:"12px" }}>
            {[
              { id:"mrz",    label:"Identificador Viejo — MRZ",   hint:"3 líneas con <<<" },
              { id:"pdf417", label:"Identificador Nuevo — PDF417", hint:"Código de barras rectangular" },
            ].map(op => (
              <button key={op.id} onClick={()=>{ setFormatoDNI(op.id); setError(""); setDniLeido(false); setPreviaDNI(null); setMrzFallback(false); setMrzManual("") }}
                style={{
                  flex:1, padding:"10px 12px", textAlign:"left",
                  background: formatoDNI===op.id ? "#1e3a5f" : "#1a1a1a",
                  border: "1px solid " + (formatoDNI===op.id ? "#3b82f6" : "#2a2a2a"),
                  borderRadius:"8px", cursor:"pointer", fontFamily:"Inter, sans-serif",
                  transition:"all 0.15s"
                }}>
                <div style={{ fontSize:"0.8rem", fontWeight:700, color: formatoDNI===op.id ? "#3b82f6" : "#6b7280" }}>
                  {formatoDNI===op.id ? "● " : "○ "}{op.label}
                </div>
                <div style={{ fontSize:"0.7rem", color:"#4b5563", marginTop:"2px" }}>{op.hint}</div>
              </button>
            ))}
          </div>

          {/* Instrucción contextual */}
          <div style={{ fontSize:"0.75rem", color:"#4b5563", marginBottom:"10px" }}>
            {formatoDNI === "mrz"
              ? "📸 Fotografiá el dorso completo — las 3 líneas con letras y números deben verse claras"
              : "📸 Fotografiá el dorso — el código de barras rectangular en el borde inferior"}
          </div>

          {/* Botón upload + previa */}
          <div style={{ display:"flex", alignItems:"center", gap:"12px" }}>
            <input ref={inputFileRef} type="file" accept="image/*" capture="environment"
              onChange={handleArchivoDNI} style={{ display:"none" }} id="dni-upload"/>
            <label htmlFor="dni-upload" style={{
              display:"inline-flex", alignItems:"center", gap:"8px",
              padding:"9px 18px",
              background: leyendoDNI ? "#1a1a1a" : "#1e3a5f",
              border:"1px solid #3b82f640", borderRadius:"8px",
              color: leyendoDNI ? "#4b5563" : "#3b82f6",
              fontFamily:"Inter, sans-serif", fontWeight:700, fontSize:"0.85rem",
              cursor: leyendoDNI ? "not-allowed" : "pointer", whiteSpace:"nowrap",
              pointerEvents: leyendoDNI ? "none" : "auto"
            }}>
              {leyendoDNI ? (
                <>
                  <span style={{ display:"inline-block", width:"14px", height:"14px", border:"2px solid #3b82f6", borderTopColor:"transparent", borderRadius:"50%", animation:"spin 0.8s linear infinite" }}/>
                  {formatoDNI === "mrz" ? "Leyendo MRZ..." : "Leyendo código..."}
                </>
              ) : "📷 Subir foto del dorso"}
            </label>
            {previaDNI && !leyendoDNI && (
              <img src={previaDNI} alt="DNI" style={{ height:"48px", borderRadius:"6px", border:"1px solid #2a2a2a", objectFit:"cover" }}/>
            )}
          </div>
        </div>

        {/* ── Fallback MRZ manual ── */}
        {mrzFallback && formatoDNI === "mrz" && (
          <div style={{ background:"#1a1200", border:"1px solid #f59e0b40", borderRadius:"10px", padding:"14px" }}>
            <div style={{ fontSize:"0.8rem", fontWeight:700, color:"#f59e0b", marginBottom:"6px" }}>
              ✏️ Pegá las 3 líneas del dorso del DNI
            </div>
            <div style={{ fontSize:"0.72rem", color:"#6b7280", marginBottom:"8px" }}>
              Copiá exactamente las 3 líneas con los {"<<<"} que están en la parte inferior del dorso
            </div>
            <textarea
              value={mrzManual}
              onChange={e => handleMrzManual(e.target.value)}
              placeholder={"IDARG32293640<5<<<<<<<<<<<<<<<<<\n8509037F3705270ARG<<<<<<<<<<<<<<0\nWEGRZYN<<AYELEN<<<<<<<<<<<<<<<<<<"}
              rows={4}
              style={{ ...INPUT_S, fontFamily:"monospace", fontSize:"0.78rem", resize:"vertical", letterSpacing:"1px" }}
            />
            {dniLeido && (
              <div style={{ fontSize:"0.8rem", color:"#10b981", fontWeight:700, marginTop:"6px" }}>✓ Datos leídos correctamente</div>
            )}
          </div>
        )}

        {/* ── Separador ── */}
        <div style={{ display:"flex", alignItems:"center", gap:"10px" }}>
          <div style={{ flex:1, height:"1px", background:"#2a2a2a" }}/>
          <span style={{ fontSize:"0.75rem", color:"#4b5563" }}>o completá manualmente</span>
          <div style={{ flex:1, height:"1px", background:"#2a2a2a" }}/>
        </div>

        {/* Nombre y Apellido */}
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"12px" }}>
          <div>
            <label style={LABEL_S}>Nombre *</label>
            <input value={form.nombre} onChange={e=>set("nombre",e.target.value)} style={INPUT_S} placeholder="Juan"/>
          </div>
          <div>
            <label style={LABEL_S}>Apellido *</label>
            <input value={form.apellido} onChange={e=>set("apellido",e.target.value)} style={INPUT_S} placeholder="Pérez"/>
          </div>
        </div>

        {/* DNI y Sexo */}
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"12px" }}>
          <div>
            <label style={LABEL_S}>DNI</label>
            <input value={form.dni} onChange={e=>set("dni",e.target.value.replace(/\D/g,""))} style={INPUT_S} placeholder="32293640" maxLength={8} inputMode="numeric"/>
          </div>
          <div>
            <label style={LABEL_S}>Sexo</label>
            <select value={form.sexo} onChange={e=>set("sexo",e.target.value)} style={sel}>
              <option value="">Seleccionar</option>
              <option value="F">Femenino</option>
              <option value="M">Masculino</option>
              <option value="X">No binario / Otro</option>
            </select>
          </div>
        </div>

        {/* Nacimiento + Vencimiento DNI */}
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"12px" }}>
          <div>
            <label style={LABEL_S}>Fecha de nacimiento</label>
            <input type="date" value={form.nacimiento} onChange={e=>set("nacimiento",e.target.value)} style={INPUT_S}
              max={new Date().toISOString().split("T")[0]}/>
          </div>
          <div>
            <label style={LABEL_S}>Vencimiento DNI</label>
            <input type="date" value={form.vencimiento} onChange={e=>set("vencimiento",e.target.value)} style={INPUT_S}
              min={new Date().toISOString().split("T")[0]}/>
          </div>
        </div>

        {/* Teléfono y Email */}
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"12px" }}>
          <div>
            <label style={LABEL_S}>Teléfono</label>
            <input value={form.telefono} onChange={e=>set("telefono",e.target.value)} style={INPUT_S} placeholder="+54 9 11 1234-5678" inputMode="tel"/>
          </div>
          <div>
            <label style={LABEL_S}>Email</label>
            <input type="email" value={form.email} onChange={e=>set("email",e.target.value)} style={INPUT_S} placeholder="juan@email.com"/>
          </div>
        </div>

        {/* CUIL */}
        <div>
          <label style={LABEL_S}>CUIL / CUIT</label>
          <div style={{ display:"flex", alignItems:"center", gap:"6px" }}>
            <input
              value={cuilPrefijo}
              onChange={e => setCuilPrefijo(e.target.value.replace(/\D/g,"").slice(0,2))}
              style={{ ...INPUT_S, width:"52px", textAlign:"center", padding:"9px 6px" }}
              placeholder="20"
              maxLength={2}
              inputMode="numeric"
            />
            <span style={{ color:"#4b5563", fontWeight:700, fontSize:"1.1rem" }}>—</span>
            <input
              value={form.dni || ""}
              readOnly
              style={{ ...INPUT_S, flex:1, color:"#6b7280", background:"#111", cursor:"default" }}
              placeholder="DNI auto"
            />
            <span style={{ color:"#4b5563", fontWeight:700, fontSize:"1.1rem" }}>—</span>
            <input
              value={form.cuil ? form.cuil.slice(-1) : ""}
              readOnly
              style={{ ...INPUT_S, width:"40px", textAlign:"center", color:"#10b981", background:"#111", cursor:"default", padding:"9px 6px" }}
              placeholder="—"
            />
          </div>
          {form.cuil?.length === 11 && (
            <div style={{ fontSize:"0.72rem", color:"#4b5563", marginTop:"4px" }}>
              ✓ CUIL calculado: {form.cuil}
            </div>
          )}
        </div>

        {/* Ciudad */}
        <div>
          <label style={LABEL_S}>Ciudad</label>
          <select value={form.ciudad} onChange={e=>setForm(p=>({...p,ciudad:e.target.value}))} style={{ ...INPUT_S, cursor:"pointer" }}>
            <option value="">Seleccionar ciudad</option>
            <option value="Bs As">Buenos Aires</option>
            <option value="Córdoba">Córdoba</option>
            <option value="Rosario">Rosario</option>
            <option value="Mendoza">Mendoza</option>
            <option value="Tucumán">Tucumán</option>
            <option value="Mar del Plata">Mar del Plata</option>
            <option value="Salta">Salta</option>
            <option value="Santa Fe">Santa Fe</option>
            <option value="Neuquén">Neuquén</option>
            <option value="Otra">Otra</option>
          </select>
          {form.ciudad === "Otra" && (
            <input
              style={{ ...INPUT_S, marginTop:"8px" }}
              placeholder="Especificar ciudad..."
              value={form.ciudad_custom || ""}
              onChange={e=>setForm(p=>({...p,ciudad_custom:e.target.value}))}
            />
          )}
        </div>

        {error && (
          <div style={{ padding:"10px 14px", background:"#2a0a0a", border:"1px solid #ef444440", borderRadius:"8px", color:"#ef4444", fontSize:"0.875rem" }}>
            {error}
          </div>
        )}

        <div style={{ display:"flex", gap:"8px" }}>
          <button onClick={onCerrar} style={{ ...BTN_GHOST, flex:1 }}>Cancelar</button>
          <button onClick={handleGuardar} disabled={guardando} style={{ ...BTN_PRIMARY, flex:2, background:guardando?"#1a1a1a":"#10b981", color:guardando?"#6b7280":"white" }}>
            {guardando ? "Guardando..." : "Crear cliente"}
          </button>
        </div>
      </div>
    </Modal>
  )
}

// ─────────────────────────────────────────────────
// ModalEditarCliente
// ─────────────────────────────────────────────────
export function ModalEditarCliente({ cliente, onGuardado, onCerrar }) {
  const [form, setForm] = useState({
    nombre:    cliente.nombre    || "",
    apellido:  cliente.apellido  || "",
    dni:       cliente.dni       || "",
    cuil:      cliente.cuil      || "",
    telefono:  cliente.telefono  || "",
    email:     cliente.email     || "",
    nacimiento:cliente.nacimiento|| "",
    sexo:      cliente.sexo      || "",
    origen:    cliente.origen    || "",
    ciudad:    cliente.ciudad    || "",
    ciudad_custom: "",
    grupo_wspp: cliente.grupo_wspp || "No",
  })
  const [guardando, setGuardando]               = useState(false)
  const [exito, setExito]                       = useState(false)
  const [confirmarEliminar, setConfirmarEliminar] = useState(false)
  const [eliminando, setEliminando]             = useState(false)
  const [textoConfirm, setTextoConfirm]         = useState("")
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }))

  const [cuilPrefijo, setCuilPrefijo] = useState(() => {
    if (cliente.cuil?.length >= 2) return cliente.cuil.slice(0,2)
    if (cliente.sexo === "M") return "20"
    if (cliente.sexo === "F") return "27"
    return "23"
  })

  useEffect(() => {
    if (cuilPrefijo.length === 2 && form.dni.length >= 7) {
      const check = calcularVerificadorCUIL(cuilPrefijo, form.dni)
      set("cuil", cuilPrefijo + form.dni.padStart(8,"0") + check)
    }
  }, [cuilPrefijo, form.dni])
  const sel = { ...INPUT_S, cursor:"pointer" }

  // Normalizar nacimiento a formato YYYY-MM-DD para el date input
  function normalizarFecha(val) {
    if (!val) return ""
    if (val.includes("-") && val.length === 10) return val // ya está bien
    if (val.includes("/")) {
      const [d, m, y] = val.split("/")
      return `${y}-${m.padStart(2,"0")}-${d.padStart(2,"0")}`
    }
    return val
  }

  async function handleGuardar() {
    setGuardando(true)
    try {
      const { ciudad_custom, ...rest } = form
      const datosGuardar = { ...rest, ciudad: form.ciudad === "Otra" ? (ciudad_custom || "") : form.ciudad, actualizado_en:new Date() }
      await updateDoc(doc(db,"usuarios_sheets",cliente.id), datosGuardar)
      setExito(true)
      setTimeout(() => { onGuardado({ ...cliente, ...datosGuardar }); onCerrar() }, 800)
    } catch(e) { console.error(e) } finally { setGuardando(false) }
  }

  async function handleEliminar() {
    if (textoConfirm.trim().toLowerCase() !== "eliminar") return
    setEliminando(true)
    try {
      for (const sub of ["viajes","vouchers"]) {
        try {
          const snap = await getDocs(collection(db,"usuarios_sheets",cliente.id,sub))
          await Promise.all(snap.docs.map(d => deleteDoc(d.ref)))
        } catch(_) {}
      }
      await deleteDoc(doc(db,"usuarios_sheets",cliente.id))
      onGuardado(null); onCerrar()
    } catch(e) { console.error(e) } finally { setEliminando(false) }
  }

  return (
    <Modal titulo={"Editar — "+cliente.nombre+" "+cliente.apellido} subtitulo={"ID "+cliente.id} onCerrar={onCerrar} ancho="600px">
      {exito ? (
        <div style={{ textAlign:"center", padding:"30px" }}>
          <div style={{ fontSize:"2rem", marginBottom:"8px" }}>✓</div>
          <div style={{ color:"#10b981", fontWeight:800 }}>Guardado</div>
        </div>
      ) : confirmarEliminar ? (
        <div>
          <div style={{ background:"#2a0a0a", border:"1px solid #ef444440", borderRadius:"10px", padding:"16px", marginBottom:"20px" }}>
            <div style={{ fontWeight:700, color:"#ef4444", marginBottom:"6px" }}>⚠️ Eliminar cliente</div>
            <div style={{ fontSize:"0.875rem", color:"#9ca3af", lineHeight:1.6 }}>
              Acción irreversible. Se eliminarán todos los datos de <strong style={{ color:"#e5e7eb" }}>{cliente.nombre} {cliente.apellido}</strong>.<br/>
              Escribí <strong style={{ color:"#ef4444" }}>eliminar</strong> para confirmar.
            </div>
          </div>
          <input placeholder="Escribí: eliminar" value={textoConfirm} onChange={e=>setTextoConfirm(e.target.value)}
            style={{ ...INPUT_S, marginBottom:"12px", border:"1px solid #ef444460" }}/>
          <div style={{ display:"flex", gap:"8px" }}>
            <button onClick={()=>setConfirmarEliminar(false)} style={{ ...BTN_GHOST, flex:1 }}>Cancelar</button>
            <button onClick={handleEliminar}
              disabled={textoConfirm.trim().toLowerCase()!=="eliminar"||eliminando}
              style={{ flex:1, padding:"10px", background:textoConfirm.trim().toLowerCase()==="eliminar"?"#ef4444":"#2a0a0a", border:"none", borderRadius:"8px", color:textoConfirm.trim().toLowerCase()==="eliminar"?"white":"#4b5563", fontFamily:"Inter, sans-serif", fontWeight:700, cursor:"pointer" }}>
              {eliminando ? "Eliminando..." : "Confirmar"}
            </button>
          </div>
        </div>
      ) : (
        <div style={{ display:"flex", flexDirection:"column", gap:"14px" }}>

          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"12px" }}>
            <div>
              <label style={LABEL_S}>Nombre *</label>
              <input value={form.nombre} onChange={e=>set("nombre",e.target.value)} style={INPUT_S}/>
            </div>
            <div>
              <label style={LABEL_S}>Apellido *</label>
              <input value={form.apellido} onChange={e=>set("apellido",e.target.value)} style={INPUT_S}/>
            </div>
          </div>

          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"12px" }}>
            <div>
              <label style={LABEL_S}>DNI</label>
              <input value={form.dni} onChange={e=>set("dni",e.target.value.replace(/\D/g,""))} style={INPUT_S} placeholder="32293640" maxLength={8} inputMode="numeric"/>
            </div>
            <div>
              <label style={LABEL_S}>Sexo</label>
              <select value={form.sexo} onChange={e=>set("sexo",e.target.value)} style={sel}>
                <option value="">Seleccionar</option>
                <option value="F">Femenino</option>
                <option value="M">Masculino</option>
                <option value="X">No binario / Otro</option>
              </select>
            </div>
          </div>

          <div>
            <label style={LABEL_S}>Fecha de nacimiento</label>
            <input type="date" value={normalizarFecha(form.nacimiento)} onChange={e=>set("nacimiento",e.target.value)} style={INPUT_S}
              max={new Date().toISOString().split("T")[0]}/>
          </div>

          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"12px" }}>
            <div>
              <label style={LABEL_S}>Teléfono</label>
              <input value={form.telefono} onChange={e=>set("telefono",e.target.value)} style={INPUT_S} placeholder="+54 9 11..."/>
            </div>
            <div>
              <label style={LABEL_S}>Email</label>
              <input type="email" value={form.email} onChange={e=>set("email",e.target.value)} style={INPUT_S} placeholder="juan@email.com"/>
            </div>
          </div>

          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"12px" }}>
            <div>
            <label style={LABEL_S}>CUIL / CUIT</label>
            <div style={{ display:"flex", alignItems:"center", gap:"6px" }}>
              <input
                value={cuilPrefijo}
                onChange={e => setCuilPrefijo(e.target.value.replace(/\D/g,"").slice(0,2))}
                style={{ ...INPUT_S, width:"52px", textAlign:"center", padding:"9px 6px" }}
                placeholder="20"
                maxLength={2}
                inputMode="numeric"
              />
              <span style={{ color:"#4b5563", fontWeight:700, fontSize:"1.1rem" }}>—</span>
              <input
                value={form.dni || ""}
                readOnly
                style={{ ...INPUT_S, flex:1, color:"#6b7280", background:"#111", cursor:"default" }}
                placeholder="DNI auto"
              />
              <span style={{ color:"#4b5563", fontWeight:700, fontSize:"1.1rem" }}>—</span>
              <input
                value={form.cuil ? form.cuil.slice(-1) : ""}
                readOnly
                style={{ ...INPUT_S, width:"40px", textAlign:"center", color:"#10b981", background:"#111", cursor:"default", padding:"9px 6px" }}
                placeholder="—"
              />
            </div>
            {form.cuil?.length === 11 && (
              <div style={{ fontSize:"0.72rem", color:"#4b5563", marginTop:"4px" }}>
                ✓ CUIL calculado: {form.cuil}
              </div>
            )}
          </div>

          <div>
            <label style={LABEL_S}>Ciudad</label>
            <select value={form.ciudad} onChange={e=>setForm(p=>({...p,ciudad:e.target.value}))} style={sel}>
                <option value="">Seleccionar ciudad</option>
                <option value="Bs As">Buenos Aires</option>
                <option value="Córdoba">Córdoba</option>
                <option value="Rosario">Rosario</option>
                <option value="Mendoza">Mendoza</option>
                <option value="Tucumán">Tucumán</option>
                <option value="Mar del Plata">Mar del Plata</option>
                <option value="Salta">Salta</option>
                <option value="Santa Fe">Santa Fe</option>
                <option value="Neuquén">Neuquén</option>
                <option value="Otra">Otra</option>
              </select>
              {form.ciudad === "Otra" && (
                <input
                  style={{ ...INPUT_S, marginTop:"8px" }}
                  placeholder="Especificar ciudad..."
                  value={form.ciudad_custom || ""}
                  onChange={e=>setForm(p=>({...p,ciudad_custom:e.target.value}))}
                />
              )}
            </div>
          </div>

          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"12px" }}>
            <div>
              <label style={LABEL_S}>Grupo WhatsApp</label>
              <select value={form.grupo_wspp||"No"} onChange={e=>set("grupo_wspp",e.target.value)} style={sel}>
                <option value="Si">Sí</option>
                <option value="No">No</option>
              </select>
            </div>
          </div>

          <button onClick={handleGuardar} disabled={guardando}
            style={{ ...BTN_PRIMARY, background:guardando?"#1a1a1a":"#3b82f6", color:guardando?"#6b7280":"white" }}>
            {guardando ? "Guardando..." : "Guardar cambios"}
          </button>

          <div style={{ borderTop:"1px solid #2a2a2a", paddingTop:"14px" }}>
            <button onClick={()=>setConfirmarEliminar(true)}
              style={{ background:"none", border:"none", color:"#4b5563", fontFamily:"Inter, sans-serif", fontSize:"0.8rem", cursor:"pointer", textDecoration:"underline" }}>
              Eliminar este cliente
            </button>
          </div>
        </div>
      )}
    </Modal>
  )
}
